import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button, Link } from '@mokoboko/components';
import { mergeClassNames } from '@/lib/utils';
import type { TourStep } from './tour-registry';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourTooltipProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  spotlightRect: SpotlightRect;
  onNext: () => void;
  onSkip: () => void;
}

export const TourTooltip = ({
  step,
  stepIndex,
  totalSteps,
  spotlightRect,
  onNext,
  onSkip,
}: TourTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const isLastStep = stepIndex === totalSteps - 1;

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  // Calculate position: below spotlight, centered horizontally, clamped to viewport
  const gap = 16;
  const arrowSize = 8;
  const sideMargin = 16;
  const maxWidth = 280;

  const top = spotlightRect.top + spotlightRect.height + gap + arrowSize;

  // Center tooltip horizontally relative to spotlight, clamp to viewport
  const spotlightCenter = spotlightRect.left + spotlightRect.width / 2;
  const tooltipWidth = Math.min(maxWidth, window.innerWidth - sideMargin * 2);
  let left = spotlightCenter - tooltipWidth / 2;
  left = Math.max(sideMargin, Math.min(left, window.innerWidth - tooltipWidth - sideMargin));

  // Arrow horizontal position relative to tooltip
  const arrowLeft = Math.max(
    20,
    Math.min(spotlightCenter - left, tooltipWidth - 20)
  );

  return (
    <div
      className="fixed z-modal"
      style={{
        top,
        left,
        width: tooltipWidth,
      }}
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
    >
      {/* Arrow pointing up */}
      <div
        className="absolute -top-2 h-4 w-4 rotate-45 bg-white"
        style={{ left: arrowLeft - 8 }}
        aria-hidden="true"
      />

      {/* Tooltip card */}
      <div
        className={mergeClassNames(
          'relative rounded-2xl bg-white p-5 shadow-xl transition-all duration-300 ease-out',
          isVisible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-2 opacity-0'
        )}
      >
        {/* Top row: emoji + progress dots (hidden for single-step tours) */}
        {(totalSteps > 1 || step.emoji) && totalSteps > 1 && (
          <div className="flex items-start justify-between">
            <span className="block text-[32px]">{step.emoji}</span>
            <div className="flex items-center gap-1.5 pt-2">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={mergeClassNames(
                    'h-1.5 rounded-full transition-all duration-300',
                    i === stepIndex
                      ? 'w-5 bg-red-400'
                      : 'w-1.5 bg-gray-200'
                  )}
                  aria-label={
                    i === stepIndex
                      ? `Step ${i + 1} of ${totalSteps}, current`
                      : `Step ${i + 1} of ${totalSteps}`
                  }
                />
              ))}
            </div>
          </div>
        )}

        <h3 className="mt-2.5 text-base font-semibold text-gray-900">{step.title}</h3>

        <p className="mt-1.5 text-sm text-gray-600">{step.body}</p>

        {/* Hint (step 3) */}
        {step.hint && (
          <p className="mt-3 text-sm font-medium text-red-400">
            {step.hint}
          </p>
        )}

        {/* Footer: skip + next */}
        <div className={mergeClassNames('mt-5 flex items-center', totalSteps > 1 ? 'justify-between' : 'justify-end')}>
          {totalSteps > 1 && (
            <Link
              color="red"
              size="sm"
              onClick={onSkip}
              role="button"
              tabIndex={0}
            >
              Skip tour
            </Link>
          )}

          {/* Next / Got it button */}
          <Button
            variant="primary"
            size="md"
            onClick={onNext}
            trailingIcon={!isLastStep ? <ChevronRight /> : undefined}
            className={totalSteps === 1 ? 'w-full justify-center' : ''}
          >
            {isLastStep ? 'Got it!' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};
